import { useState, useLayoutEffect, useContext, useCallback, useMemo } from 'react';
import { Typography, LinearProgress, IconButton, Tooltip, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTour } from '../components/common/Tour/useTour';
import { matPageTourConfig } from '../components/mat/matPageTour';
import TopBar from '../components/common/TopBar';
import { useSetTitle } from '../components/hooks/useSetTitle';
import MATHeader from '../components/mat/MATHeader';
import MATGrid from '../components/mat/MATGrid';
import MATActionsBar from '../components/mat/MATActionsBar';
import MATFilterChips from '../components/mat/MATFilterChips';
import useSetOrg from '../components/hooks/useSetOrg';
import { useParams, useLocation, useNavigate } from 'react-router';
import type { VendorExistsFilter } from '../components/mat/MATGrid.types';
import { AppContext } from '../App';

function MATPage() {
  useSetTitle('MAT Management');
  const { setToast } = useContext(AppContext);

  const onTourComplete = useCallback(() => {
    setToast({ open: true, message: 'Tour complete! You\'re all set.', variant: 'success' });
  }, [setToast]);

  const { startTour } = useTour(matPageTourConfig, { onComplete: onTourComplete });

  const { dpid } = useParams() as { dpid?: string };
  const location = useLocation();
  const navigate = useNavigate();

  const { loading: loadingOrg } = useSetOrg(dpid);

  const [matCount, setMatCount] = useState<number | null>(null);
  const [discoveredCount, setDiscoveredCount] = useState<number | null>(null);
  const [refreshGrid, setRefreshGrid] = useState<(() => void) | null>(null);
  const [hasDiscoveredRowsMissingClient, setHasDiscoveredRowsMissingClient] = useState(false);

  // Memoised so the grid keeps a stable prop across the re-render each page fetch causes.
  const handleTotalCountChange = useCallback((matEntries: number, discovered: number) => {
    setMatCount(matEntries);
    setDiscoveredCount(discovered);
  }, []);

  // One-time: ensure URL has default checkbox params for bookmarkability.
  // Uses useLayoutEffect so defaults are seeded before the first paint,
  // preventing a brief render with un-seeded URL params.
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    if (params.get('includeMatData') === null) {
      params.set('includeMatData', 'true');
      changed = true;
    }
    if (params.get('includeDiscovered') === null) {
      params.set('includeDiscovered', 'false');
      changed = true;
    }
    if (params.get('includeReviewedOnly') === null) {
      params.set('includeReviewedOnly', 'false');
      changed = true;
    }
    if (changed) {
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive checkbox states directly from URL (single source of truth)
  // No bidirectional useState↔URL sync — eliminates oscillation loops
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const includeMatData = useMemo(() => {
    const value = searchParams.get('includeMatData');
    return value === null ? true : value === 'true';
  }, [searchParams]);

  const includeDiscovered = useMemo(() => {
    const value = searchParams.get('includeDiscovered');
    return value === null ? false : value === 'true';
  }, [searchParams]);

  const includeReviewedOnly = useMemo(() => {
    const value = searchParams.get('includeReviewedOnly');
    return value === null ? false : value === 'true';
  }, [searchParams]);

  const vendorExistsFilter = useMemo((): VendorExistsFilter => {
    const value = searchParams.get('vendorExists') as VendorExistsFilter | null;
    return value === 'true' || value === 'false' ? value : 'all';
  }, [searchParams]);

  // Handlers that directly update the URL — no intermediate state
  const updateCheckboxParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(window.location.search);
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate, location.pathname]
  );

  const setIncludeMatData = useCallback(
    (value: boolean) => updateCheckboxParam('includeMatData', value ? 'true' : 'false'),
    [updateCheckboxParam]
  );

  const setIncludeDiscovered = useCallback(
    (value: boolean) => updateCheckboxParam('includeDiscovered', value ? 'true' : 'false'),
    [updateCheckboxParam]
  );

  const setIncludeReviewedOnly = useCallback(
    (value: boolean) => updateCheckboxParam('includeReviewedOnly', value ? 'true' : 'false'),
    [updateCheckboxParam]
  );

  const setVendorExistsFilter = useCallback(
    (value: VendorExistsFilter) =>
      updateCheckboxParam('vendorExists', value === 'all' ? null : value),
    [updateCheckboxParam]
  );

  return (
    <div id="training-page">
      <TopBar />
      {loadingOrg && (
        <div style={{ width: '100%', position: 'fixed', top: 0, left: 0, zIndex: 1000 }}>
          <div style={{ height: 4 }}>
            <LinearProgress />
          </div>
        </div>
      )}
      <MATHeader />
      <div style={{ padding: '0 40px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            minHeight: 48,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            <Typography
              variant="body2"
              sx={{ color: '#555', display: 'flex', alignSelf: 'flex-end', paddingBottom: '3px' }}
              data-cy="mat-total-count"
            >
              {matCount != null || discoveredCount != null
                ? `MAT Entries: ${(matCount || 0).toLocaleString()} / Discovered: ${(discoveredCount || 0).toLocaleString()}`
                : ''}
            </Typography>
            <Tooltip title="Take a tour of this page" placement="top">
              <IconButton size="small" onClick={startTour} data-cy="btn-mat-tour" sx={{ mb: '-2px' }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          <MATActionsBar
            includeMatData={includeMatData}
            includeDiscovered={includeDiscovered}
            includeReviewedOnly={includeReviewedOnly}
            vendorExistsFilter={vendorExistsFilter}
            onIncludeMatDataChange={setIncludeMatData}
            onIncludeDiscoveredChange={setIncludeDiscovered}
            onIncludeReviewedOnlyChange={setIncludeReviewedOnly}
            onVendorExistsFilterChange={setVendorExistsFilter}
            onRefresh={refreshGrid}
          />
        </div>
      </div>
      <div style={{ padding: '0 40px' }}>
        <MATFilterChips />
      </div>
      {hasDiscoveredRowsMissingClient && (
        <Box
          sx={{
            mx: 5,
            mb: 1,
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 1,
            bgcolor: '#FFF3E0',
            border: '1px solid #FFB74D',
          }}
          data-cy="warning-discovered-missing-client"
        >
          <WarningAmberIcon sx={{ color: '#E65100', fontSize: 24 }} />
          <Typography variant="body2" sx={{ color: '#E65100' }}>
            Some discovered rows are missing client. Set client before creating MAT entries.
          </Typography>
        </Box>
      )}
      <div style={{ padding: '5px 40px 20px' }}>
        <MATGrid
          onTotalCountChange={handleTotalCountChange}
          onMissingClientChange={setHasDiscoveredRowsMissingClient}
          includeMatData={includeMatData}
          includeDiscovered={includeDiscovered}
          includeReviewedOnly={includeReviewedOnly}
          vendorExistsFilter={vendorExistsFilter}
          onRefreshNeeded={setRefreshGrid}
        />
      </div>
    </div>
  );
}
export default MATPage;
